import type { IntlShape } from 'react-intl';

import type {
  FilterValues,
  SetValueProps,
} from '@src/components/FilterDropDown/FilterDropDown.interfaces';
import { formatDaysOfWeek, normalizeDaysOfWeek } from '@src/packages/utils/dates/formatDaysOfWeek';
import { translateString } from '@src/util/intl/translateString';

import { SHIFT_STATUS } from './constants';
import type { ShiftFiltersState } from './types';

export const SHIFT_LIST_FILTER_FIELDS = {
  SEARCH_TERM: 'searchTerm',
  SHIFT_TYPE: 'shiftType',
  SELECTED_DAYS: 'selectedDays',
  STATUS: 'status',
  EXCEPTIONAL: 'exceptional',
} as const;

export const SHIFT_FILTER_DEFAULTS = {
  SHIFT_TYPE: '',
  STATUS: 'all',
  EXCEPTIONAL: null,
} as const;

const getShiftStatusMessageId = (status: string): string => {
  switch (status) {
    case SHIFT_STATUS.ACTIVE:
      return 'shifts.table.status.active';
    case SHIFT_STATUS.PENDING:
      return 'shifts.table.status.pending';
    default:
      return 'shifts.filter.status.all';
  }
};

export const getShiftStatusFilterLabel = (status: string, intl: IntlShape): string =>
  intl.formatMessage({ id: getShiftStatusMessageId(status) });

export const INIT_SHIFT_LIST_FILTERS: FilterValues = {
  [SHIFT_LIST_FILTER_FIELDS.SEARCH_TERM]: {
    value: '',
    label: translateString('shifts.filter.searchPlaceholder'),
    valueType: 'string',
  },
  [SHIFT_LIST_FILTER_FIELDS.SHIFT_TYPE]: {
    value: SHIFT_FILTER_DEFAULTS.SHIFT_TYPE,
    label: translateString('shifts.filter.type'),
    valueType: 'string',
    clearValue: SHIFT_FILTER_DEFAULTS.SHIFT_TYPE,
    hideWhenCleared: true,
  },
  [SHIFT_LIST_FILTER_FIELDS.SELECTED_DAYS]: {
    value: [],
    label: translateString('shifts.filter.days'),
    valueType: 'array',
    clearValue: [],
  },
  [SHIFT_LIST_FILTER_FIELDS.STATUS]: {
    value: SHIFT_STATUS.ACTIVE,
    label: translateString('shifts.filter.status'),
    valueTranslated: translateString(getShiftStatusMessageId(SHIFT_STATUS.ACTIVE)),
    valueType: 'string',
    clearValue: SHIFT_FILTER_DEFAULTS.STATUS,
    hideWhenCleared: true,
  },
  [SHIFT_LIST_FILTER_FIELDS.EXCEPTIONAL]: {
    value: SHIFT_FILTER_DEFAULTS.EXCEPTIONAL,
    label: translateString('shifts.table.exceptional'),
    valueType: 'boolean',
    clearValue: SHIFT_FILTER_DEFAULTS.EXCEPTIONAL,
    hideWhenCleared: true,
  },
};

export const formatShiftDaysForTag = (days: unknown): string => formatDaysOfWeek(days);

export const filterValuesToShiftFilters = (values: FilterValues): ShiftFiltersState => {
  const rawDays = values[SHIFT_LIST_FILTER_FIELDS.SELECTED_DAYS]?.value;
  const selectedDays = Array.isArray(rawDays) ? normalizeDaysOfWeek(rawDays) : [];
  const exceptionalValue = values[SHIFT_LIST_FILTER_FIELDS.EXCEPTIONAL]?.value;

  return {
    searchTerm: String(values[SHIFT_LIST_FILTER_FIELDS.SEARCH_TERM]?.value ?? ''),
    shiftType: String(
      values[SHIFT_LIST_FILTER_FIELDS.SHIFT_TYPE]?.value ?? SHIFT_FILTER_DEFAULTS.SHIFT_TYPE,
    ),
    selectedDays,
    status: String(values[SHIFT_LIST_FILTER_FIELDS.STATUS]?.value ?? SHIFT_STATUS.ACTIVE),
    exceptional:
      typeof exceptionalValue === 'boolean'
        ? exceptionalValue
        : SHIFT_FILTER_DEFAULTS.EXCEPTIONAL,
  };
};

export const buildShiftFilterSetValueProps = (
  key: string,
  value: unknown,
  intl: IntlShape,
  valueTranslated?: string,
): SetValueProps => {
  switch (key) {
    case SHIFT_LIST_FILTER_FIELDS.SEARCH_TERM:
      return {
        key,
        value: String(value ?? ''),
        label: intl.formatMessage({ id: 'shifts.filter.searchPlaceholder' }),
        valueTranslated: String(value ?? ''),
        valueType: 'string',
      };
    case SHIFT_LIST_FILTER_FIELDS.SHIFT_TYPE:
      return {
        key,
        value: String(value ?? SHIFT_FILTER_DEFAULTS.SHIFT_TYPE),
        label: intl.formatMessage({ id: 'shifts.filter.type' }),
        valueTranslated: valueTranslated ?? intl.formatMessage({ id: 'shifts.type.all' }),
        valueType: 'string',
        clearValue: SHIFT_FILTER_DEFAULTS.SHIFT_TYPE,
        hideWhenCleared: true,
      };
    case SHIFT_LIST_FILTER_FIELDS.SELECTED_DAYS: {
      const days = Array.isArray(value) ? value.map(String) : [];
      return {
        key,
        value: days,
        label: intl.formatMessage({ id: 'shifts.filter.days' }),
        valueTranslated: formatShiftDaysForTag(days),
        valueType: 'array',
        clearValue: [],
      };
    }
    case SHIFT_LIST_FILTER_FIELDS.STATUS:
      return {
        key,
        value: String(value ?? SHIFT_FILTER_DEFAULTS.STATUS),
        label: intl.formatMessage({ id: 'shifts.filter.status' }),
        valueTranslated:
          valueTranslated ??
          getShiftStatusFilterLabel(String(value ?? SHIFT_FILTER_DEFAULTS.STATUS), intl),
        valueType: 'string',
        clearValue: SHIFT_FILTER_DEFAULTS.STATUS,
        hideWhenCleared: true,
      };
    case SHIFT_LIST_FILTER_FIELDS.EXCEPTIONAL:
      return {
        key,
        value: typeof value === 'boolean' ? value : SHIFT_FILTER_DEFAULTS.EXCEPTIONAL,
        label: intl.formatMessage({ id: 'shifts.table.exceptional' }),
        valueTranslated:
          typeof value === 'boolean'
            ? intl.formatMessage({ id: value ? 'button.yes' : 'button.no' })
            : '',
        valueType: 'boolean',
        clearValue: SHIFT_FILTER_DEFAULTS.EXCEPTIONAL,
        hideWhenCleared: true,
      };
    default:
      return { key, value: value as SetValueProps['value'], valueType: 'string' };
  }
};
